import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment';
import { useHistory, NavLink } from 'react-router-dom';

import { getPatientServiceRequest } from '../../../../api';
import TextLoader from '../../../components/global/Loader/text';
import Loader from '../../../components/global/Loader';
import EmptyData from '../../../components/global/emptyData';
import Pagination from '../../../components/global/List/Pagination';
import { useUrlParser } from '../../../../utils/useUrlParser';

// Helper function to validate dates
const isValidDate = (dateString) => {
  if (!dateString) return false;
  if (dateString === '0000-00-00 00:00:00' || dateString === '0000-00-00') return false;
  return moment(dateString).isValid();
};

const PageView = ({ patient }) => {

  // Helper function to determine border color based on status
  const getBorderColorClass = (status) => {
    if (!status) return 'status-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active')) return 'status-active';
    if (statusLower.includes('completed')) return 'status-completed';
    return 'status-other';
  };
  
  // Helper function to determine status style class
  const getStatusBadgeClass = (status) => {
    if (!status) return 'badge-default';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active')) return 'badge-active';
    if (statusLower.includes('completed')) return 'badge-completed';
    return 'badge-other';
  };
  
  const [pageData, setPageData] = useState(null);
  const [pageDataLoading, setPageDataLoading] = useState(false);
  const { urlData } = useUrlParser();
  const [searchNumber, setSearchNumber] = useState(urlData.params?.number || '');
  const history = useHistory();
  
  useEffect(() => {
    setPageDataLoading(true);
    
    if (patient.data) {
      const page = +urlData.params.page > 0 && Number.isInteger(+urlData.params.page) ? +urlData.params.page : 1;
      
      getPatientServiceRequest(patient.data.id, page, urlData.params?.number).then((response) => {
        setPageData(response);
        setPageDataLoading(false);
      });

    }
  }, [patient.data, urlData.params.page, urlData.params.number]);

  if (pageDataLoading) return <TextLoader />; 

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const page = 1; // Reset to first page on search
    const query = searchNumber ? `?page=${page}&number=${searchNumber}` : `?page=${page}`;
    history.push(`${urlData.path}${query}`);
  };
  
  const handleClearSearch = () => {
    setSearchNumber('');
    history.push(`${urlData.path}?page=1`);
  };

  // Format input with dashes as user types
  const handleInputChange = (e) => {
    const value = e.target.value;
    console.log('Input value:', value);
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format with dashes
    let formattedValue = '';
    for (let i = 0; i < digitsOnly.length && i < 16; i++) {
      if (i > 0 && i % 4 === 0) {
        formattedValue += '-';
      }
      formattedValue += digitsOnly[i];
    }
    
    setSearchNumber(formattedValue);
  };

  return (
    <div className="personal-recipes-wrapper">
      
      <div className="personal-recipes">
        <div className="personal-recipes-title">Ваші направлення</div>
        
        <div className="service-request-search">
          <form onSubmit={handleSearchSubmit}>
            <div className="search-container">
              <input
                id="searchNumber"
                type="text"
                value={searchNumber}
                onChange={handleInputChange}
                placeholder="Пошук направлення за номером"
                className="search-input"
                maxLength="40"
                required
              />
              <button type="submit" className="search-button">
                <i className="fas fa-search" />
              </button>
              {urlData.params?.number && (
                <button 
                  type="button" 
                  onClick={handleClearSearch} 
                  className="clear-search-button"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </form>
        </div>

        {(!Object.keys(pageData?.data ?? []).length ? <EmptyData /> : (
          <ul className="personal-recipes-data-list">
            {pageData.data.map(({ id, created_at, inserted_at, status, status_value, code, category, service_request_number, occurrence_period_start, occurrence_period_end, requester_employee_name }) => {
              const validStartDate = isValidDate(occurrence_period_start);
              const validEndDate = isValidDate(occurrence_period_end);
              const borderClass = getBorderColorClass(status);
              const badgeClass = getStatusBadgeClass(status);
              
              return (
                <li className={`personal-recipes-data-list__item service-request-item ${borderClass}`} key={id}>
                  <div className="service-request-title">{code}</div>
                  
                  <div className="service-request-grid">
                    <div className="service-request-field">
                      <div className="service-request-label">Категорія</div>
                      <div className="service-request-value">{category}</div>
                    </div>
                    
                    <div className="service-request-field">
                      <div className="service-request-label">Статус</div>
                      <div className="service-request-value">
                        <span className={badgeClass}>{status_value}</span>
                      </div>
                    </div>
                    
                    <div className="service-request-field">
                      <div className="service-request-label">Номер направлення</div>
                      <div className="service-request-value">
                        <NavLink to={`/office/service-requests?page=1&number=${service_request_number}`} onClick={() => {setSearchNumber(service_request_number)}}>
                            {service_request_number}
                          </NavLink>
                      </div>
                    </div>
                    
                    <div className="service-request-field">
                      <div className="service-request-label">Дата створення</div>
                      <div className="service-request-value date-with-icon">
                        <i className="fas fa-calendar-alt" /> 
                        {moment(inserted_at || created_at).format('DD.MM.YYYY')}
                      </div>
                    </div>
                    
                    {requester_employee_name && (
                      <div className="service-request-field">
                        <div className="service-request-label">Лікар</div>
                        <div className="service-request-value">{requester_employee_name}</div>
                      </div>
                    )}
                    
                    {(validStartDate || validEndDate) && (
                      <div className="service-request-field">
                        <div className="service-request-label">Термін дії</div>
                        <div className="service-request-value">
                          {validStartDate && moment(occurrence_period_start).format('DD.MM.YYYY')}
                          {validEndDate && ' до'}
                          {validEndDate && moment(occurrence_period_end).format('DD.MM.YYYY')}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ))}
      </div>
      
      <Loader isVisible={pageDataLoading} />

      {(!Object.keys(pageData?.data ?? []).length ? null : (
        <Pagination
          currentPage={pageData.pagination.currentPage}
          totalPages={pageData.pagination.pageCount}
          pageName="page"
        />
      ))}
    </div>
  );
};

export default PageView;
